// src/pages/ActivitiesBuilder/components/SavedActivities.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  IconButton,
  Chip,
  Divider,
  Rating,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

import { Trip } from '../../../contexts/ItineraryContext';
import { useActivity, Activity } from '../../../contexts/ActivityContext';
import ActivityDetailDialog from './ActivityDetailDialog';

interface SavedActivitiesProps {
  trip: Trip;
  onSuccess: (message: string) => void;
}

const SavedActivities: React.FC<SavedActivitiesProps> = ({ trip, onSuccess }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [filteredActivities, setFilteredActivities] = useState<Activity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Get activities context
  const { 
    savedActivities, 
    fetchActivities, 
    unsaveActivity,
    deleteActivity
  } = useActivity();

  // Filter activities
  useEffect(() => {
    const filterActivities = () => {
      const term = searchTerm.toLowerCase();
      
      return savedActivities.filter(activity => {
        const matchesSearch = !term || 
          activity.title.toLowerCase().includes(term) ||
          activity.description.toLowerCase().includes(term) ||
          activity.location.toLowerCase().includes(term);
          
        const matchesType = selectedType === 'all' || activity.type === selectedType;
        const matchesTrip = !trip.id || activity.tripId === trip.id;
        
        return matchesSearch && matchesType && matchesTrip;
      });
    };
    
    setFilteredActivities(filterActivities());
  }, [savedActivities, searchTerm, selectedType, trip.id]);

  // Fetch activities
  useEffect(() => {
    const loadActivities = async () => {
      setLoading(true);
      await fetchActivities(trip.id);
      setLoading(false);
    };
    
    loadActivities();
  }, [fetchActivities, trip.id]);

  // Handle search clear
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle type filter change
  const handleTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedType(event.target.value as string);
  };

  // Handle unsave
  const handleUnsave = async (activity: Activity) => {
    const success = await unsaveActivity(activity.id);
    if (success) {
      onSuccess('Activity removed from saved activities');
    }
  };

  // Handle delete
  const handleDelete = async (activity: Activity) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      const success = await deleteActivity(activity.id);
      if (success) {
        onSuccess('Activity deleted successfully');
      }
    }
  };
  
  // Open activity details
  const handleOpenDetails = (activity: Activity) => {
    setSelectedActivity(activity);
    setDetailDialogOpen(true);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header & Filters */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Saved Activities
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search saved activities..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="activity-type-label">Type</InputLabel>
            <Select
              labelId="activity-type-label"
              id="activity-type"
              value={selectedType}
              label="Type"
              onChange={handleTypeChange as any}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="activity">Activities</MenuItem>
              <MenuItem value="food">Food & Dining</MenuItem>
              <MenuItem value="accommodation">Accommodation</MenuItem>
              <MenuItem value="transportation">Transportation</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredActivities.length > 0 ? (
          <Grid container spacing={2}>
            {filteredActivities.map((activity) => (
              <Grid item xs={12} sm={6} md={4} key={activity.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {activity.imageUrl && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={activity.imageUrl}
                      alt={activity.title}
                    />
                  )}
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" component="div" noWrap>
                        {activity.title}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(activity)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      my: 1
                    }}>
                      {activity.description}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Location:</strong> {activity.location}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      {activity.rating && (
                        <Rating value={activity.rating} precision={0.5} size="small" readOnly />
                      )}
                      
                      {activity.cost !== undefined && (
                        <Typography variant="body2" fontWeight="bold">
                          ${activity.cost.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip 
                        label={activity.type.charAt(0).toUpperCase() + activity.type.slice(1)} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                      
                      {activity.duration && (
                        <Chip label={activity.duration} size="small" variant="outlined" />
                      )}
                      
                      {activity.source && (
                        <Chip 
                          label={activity.source === 'ai' ? 'AI Generated' : 
                                activity.source === 'search' ? 'Search Result' : 'Custom'} 
                          size="small" 
                        />
                      )}
                    </Box>
                  </CardContent>
                  
                  <CardActions>
                    <Button size="small" onClick={() => handleOpenDetails(activity)}>
                      View Details
                    </Button>
                    <Button 
                      size="small" 
                      color="primary"
                      onClick={() => handleUnsave(activity)}
                      startIcon={<BookmarkBorderIcon />}
                    >
                      Unsave
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : savedActivities.length > 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1">
              No activities match your search criteria.
            </Typography>
            <Button onClick={() => { setSearchTerm(''); setSelectedType('all'); }} sx={{ mt: 2 }}>
              Clear Filters
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              No Saved Activities Yet
            </Typography>
            <Typography variant="body1" paragraph>
              Search for activities or generate ideas with AI, then save the ones you like.
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Activity Detail Dialog */}
      {selectedActivity && (
        <ActivityDetailDialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          activity={selectedActivity}
          onSave={handleUnsave}
          trip={trip}
        />
      )}
    </Box>
  );
};

export default SavedActivities;